import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Form from "src/components/Form"

import { getImportConfig } from "src/api/DromoImports"

import DromoImporter from "../DromoImporter"
import { getContractImportConfig } from "../configs/contracts"
import { getCustomerImportConfig } from "../configs/customers"
import { getGeneralLedgerCodesImportConfig } from "../configs/general_ledger_codes"
import { getInventoryQuantityAdjustmentConfig } from "../configs/inventory_quantity_adjustment"
import { getItemImportConfig } from "../configs/items"
import { getLongTermWaitlistConfig } from "../configs/long_term_waitlist"
import { getMetersImportConfig } from "../configs/meters"
import { getReservationImportConfig } from "../configs/reservations"
import { getSpacesImportConfig } from "../configs/spaces"

const IMPORT_TYPES = [
  { value: "contracts", label: "Contracts" },
  { value: "customers", label: "Customers" },
  { value: "general_ledger_codes", label: "G/L Codes" },
  {
    value: "inventory_quantity_adjustment",
    label: "Inventory Quantity Adjustment",
  },
  { value: "items", label: "Items" },
  { value: "long_term_waitlist", label: "Long Term Waitlist" },
  { value: "meters", label: "Meters" },
  { value: "reservations", label: "Reservations" },
  { value: "spaces", label: "Spaces" },
]

const getImporterConfig = (importType, selectOptionsData) => {
  switch (importType) {
    case "contracts":
      return getContractImportConfig(selectOptionsData)
    case "customers":
      return getCustomerImportConfig(selectOptionsData)
    case "general_ledger_codes":
      return getGeneralLedgerCodesImportConfig(selectOptionsData)
    case "items":
      return getItemImportConfig(selectOptionsData)
    case "inventory_quantity_adjustment":
      return getInventoryQuantityAdjustmentConfig(selectOptionsData)
    case "long_term_waitlist":
      return getLongTermWaitlistConfig(selectOptionsData)
    case "meters":
      return getMetersImportConfig(selectOptionsData)
    case "reservations":
      return getReservationImportConfig(selectOptionsData)
    case "spaces":
      return getSpacesImportConfig(selectOptionsData)
    default:
      throw new Error(`Unknown import type: ${importType}`)
  }
}

const ImportForm = ({ availableImportTypes }) => {
  const { marinaSlug } = useMarina()
  const [selectedType, setSelectedType] = useState("")
  const [isImporterOpen, setIsImporterOpen] = useState(false)

  const {
    data: selectOptionsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["marina", marinaSlug, "importConfig", selectedType],
    queryFn: () => getImportConfig(selectedType),
    enabled: !!selectedType,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const handleImportComplete = () => {
    setIsImporterOpen(false)
    setSelectedType("")
  }

  const isImportDisabled = !selectedType || isLoading || !selectOptionsData

  const renderImporter = () => {
    if (!isImporterOpen || !selectOptionsData) return null

    const { fields, columnHooks, rowHooks, stepHooks } = getImporterConfig(
      selectedType,
      selectOptionsData
    )

    return (
      <DromoImporter
        importType={selectedType}
        fields={fields}
        columnHooks={columnHooks}
        rowHooks={rowHooks}
        stepHooks={stepHooks}
        onClose={handleImportComplete}
      />
    )
  }

  const renderItemsImportAlert = () => {
    return (
      <AlertBanner type="info">
        Import marina items (aka products).{" "}
        <span className="font-bold">
          This will only support importing &quot;Standard&quot; Item types.
        </span>{" "}
        This means you <span className="font-bold">cannot</span> import any
        other item type such as Fuel or Metered Electric at this time.
      </AlertBanner>
    )
  }

  const renderLongTermWaitlistImportAlert = () => {
    return (
      <AlertBanner type="info">
        Import entries to your long term waitlist. Note: When you provide a
        &apos;Requested At Date&apos; for an entry, any entries sharing the same
        will &apos;Requested At Date&apos; will get ordered in Dockwa in the
        order you provided them in your sheet, starting from a default time of
        8am in the marina&apos;s local timezone. If you don&apos;t provide a
        &apos;Requested At Date&apos; we will treat the entries as having been
        requested on the day you&apos;re performing the import, while
        maintaining the order from your import.
      </AlertBanner>
    )
  }

  return (
    <div className="mb-6">
      <div className="mb-6 flex items-center justify-end gap-4">
        <span className="text-xl font-semibold">New Import</span>
        <div className="w-48">
          <Form.Select
            id="import-type"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            hasErrors={isError}
          >
            <option value="">Select import type</option>
            {IMPORT_TYPES.filter((type) =>
              availableImportTypes.includes(type.value)
            ).map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <Button
          variant="primary"
          disabled={isImportDisabled}
          onClick={() => setIsImporterOpen(true)}
        >
          Import
        </Button>
        {renderImporter()}
      </div>
      {selectedType === "items" ? renderItemsImportAlert() : null}
      {selectedType === "long_term_waitlist"
        ? renderLongTermWaitlistImportAlert()
        : null}
      {isError && (
        <div className="mb-6 flex justify-end">
          <span className="text-sm text-red-600">Failed to load config</span>
        </div>
      )}
      <hr className="border-gray-200" />
    </div>
  )
}

ImportForm.propTypes = {
  availableImportTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
}

export default ImportForm
